import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Card, Col, Collapse, Form, Row, Spinner, Table,
} from 'react-bootstrap';
import QtyDual from '../../utils/inventoryUnit';
import PageLayout from '../../layouts/PageLayout';
import { inventoryAPI, menuItemAPI } from '../../services';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthProvider';
import { toast } from 'react-toastify';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import MaterialFormModal from '../../components/modals/MaterialFormModal';
import SupplierFormModal from '../../components/modals/SupplierFormModal';
import ImportStockModal from '../../components/modals/ImportStockModal';
import BulkImportModal from '../../components/modals/BulkImportModal';
import TransferStockModal from '../../components/modals/TransferStockModal';
import RecipeFormModal from '../../components/modals/RecipeFormModal';
import StocktakeDetailModal from '../../components/modals/StocktakeDetailModal';
import MaterialTransactionsModal from '../../components/modals/MaterialTransactionsModal';
import ImportRequestDetailModal from '../../components/modals/ImportRequestDetailModal';
import RejectImportRequestModal from '../../components/modals/RejectImportRequestModal';
import MaterialPackageFormModal from '../../components/modals/MaterialPackageFormModal';
import BranchMinStockOverrideModal from '../../components/modals/BranchMinStockOverrideModal';
import BranchParLevelOverrideModal from '../../components/modals/BranchParLevelOverrideModal';
import ApplyRatioModal from '../../components/modals/ApplyRatioModal';
import InitialStockCountModal from '../../components/modals/InitialStockCountModal';
import BtpRecipeModal from '../../components/modals/BtpRecipeModal';
import ProductionModal from '../../components/modals/ProductionModal';
import SupplierMaterialModal from '../../components/modals/SupplierMaterialModal';
import { purchaseOrderAPI } from '../../services/purchaseOrder';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import InventoryShrinkageTab from './InventoryShrinkageTab';
import ShiftMaterialCheckTab from './ShiftMaterialCheckTab';

// ── Route <-> tab: mỗi tab cũ nay là 1 route riêng /inventory/<tabKey> ────────
// (việc #194334 — tách 12 tab dồn 1 trang ra menu, xem sidenavs.json "Quản lý kho")

const DEFAULT_TAB_KEY = 'stock';

// title = tiêu đề hiển thị trên trang; group = tên nhóm cha trong menu (null = không thuộc nhóm nào)
const TAB_META = {
  'stock': { title: 'Tồn kho', group: null },
  'materials': { title: 'Danh mục NVL', group: 'Nguyên vật liệu' },
  'packages': { title: 'Quy cách đóng gói', group: 'Nguyên vật liệu' },
  'recipes': { title: 'Công thức', group: 'Nguyên vật liệu' },
  'btp-recipe': { title: 'Công thức BTP', group: 'Nguyên vật liệu' },
  'suppliers': { title: 'Nhà cung cấp', group: 'Nhập hàng & Nhà cung cấp' },
  'purchase-orders': { title: 'Đặt hàng NCC', group: 'Nhập hàng & Nhà cung cấp' },
  'import-requests': { title: 'Phiếu nhập', group: 'Nhập hàng & Nhà cung cấp' },
  'stocktake': { title: 'Kiểm kê định kỳ', group: 'Kiểm kê' },
  'shift-material-check': { title: 'Kiểm NVL theo ca', group: 'Kiểm kê' },
  'shrinkage': { title: 'Hao hụt NVL', group: 'Kiểm kê' },
};

// "Báo cáo kho" không còn là tab/menu riêng (#220622) — gộp vào bên trong tab "Tồn kho" dưới dạng
// khối gập/mở. Route cũ /inventory/reports vẫn sống, chỉ đổi hướng sang /inventory/stock kèm
// ?section=reports để tự mở đúng khối báo cáo, không để 404 (có thể đã có người bookmark).
const REPORTS_TAB_KEY = 'reports';

const VALID_TAB_KEYS = [...Object.keys(TAB_META), REPORTS_TAB_KEY];

// Đường link cũ `/inventory?tab=xxx&menu_item_id=yyy` (dùng trước #194334) vẫn phải sống —
// chuyển sang route mới `/inventory/xxx?menu_item_id=yyy`, không để 404.
export function InventoryLegacyRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedTab = params.get('tab');
  const tabKey = VALID_TAB_KEYS.includes(requestedTab) ? requestedTab : DEFAULT_TAB_KEY;
  params.delete('tab');
  const rest = params.toString();
  return <Navigate to={`/inventory/${tabKey}${rest ? `?${rest}` : ''}`} replace />;
}

const fmtQty = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('vi-VN') : '—';

const CATEGORY_LABELS = {
  FOOD: 'Thực phẩm',
  BEVERAGE: 'Đồ uống',
  PACKAGING: 'Bao bì',
  CLEANING: 'Vệ sinh',
  OTHER: 'Khác',
};

// ── Tab 1: Branch Stock ──────────────────────────────────────────────────────

function BranchStockTab({ branchId, branches, allMaterials, suppliers, onRefresh, refreshKey }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [historyMaterial, setHistoryMaterial] = useState(null);
  const [overrideStock, setOverrideStock] = useState(null);
  const [parLevelOverrideStock, setParLevelOverrideStock] = useState(null);
  const [showApplyRatio, setShowApplyRatio] = useState(false);
  // #011206 — dòng tồn đang mở modal "kiểm kê khởi tạo" (đặt tồn về số đếm được).
  const [countStock, setCountStock] = useState(null);

  const materialById = useMemo(
    () => new Map((allMaterials || []).map((m) => [m.id, m])),
    [allMaterials],
  );

  const fetchStock = useCallback(async () => {
    if (!branchId) return;
    try {
      setLoading(true);
      const data = await inventoryAPI.getBranchStock(branchId);
      setStock(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải tồn kho');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { fetchStock(); }, [fetchStock, refreshKey]);

  const handleModalSuccess = () => {
    fetchStock();
    if (onRefresh) onRefresh();
    setShowImport(false);
    setShowBulk(false);
    setShowTransfer(false);
  };

  if (!branchId) return <p className="text-muted py-4 text-center">Vui lòng chọn chi nhánh để xem tồn kho</p>;

  return (
    <>
      <div className="d-flex gap-2 mb-3">
        <Button variant="success" size="sm" onClick={() => setShowImport(true)}>+ Nhập kho</Button>
        <Button variant="outline-success" size="sm" onClick={() => setShowBulk(true)}>Nhập bulk</Button>
        <Button variant="outline-warning" size="sm" onClick={() => setShowTransfer(true)}>Chuyển kho</Button>
        {isAdmin && (
          <Button variant="outline-primary" size="sm" onClick={() => setShowApplyRatio(true)}>
            Áp tỷ lệ hàng loạt
          </Button>
        )}
        <Button variant="outline-secondary" size="sm" onClick={fetchStock}>↻ Làm mới</Button>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : stock.length === 0 ? (
        <p className="text-muted text-center py-4">Chưa có dữ liệu tồn kho</p>
      ) : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>NVL</th>
              <th className="text-end">Lý thuyết</th>
              <th className="text-end">Thực tế</th>
              <th className="text-end">Ngưỡng tối thiểu</th>
              <th className="text-end">Định mức</th>
              <th>Trạng thái</th>
              <th>Kiểm kê lần cuối</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {stock.map((s) => {
              const mat = materialById.get(s.material_id) ?? s.material;
              return (
                <tr key={s.id} className={s.is_low_stock ? 'table-danger' : ''}>
                  <td className="fw-medium">{s.material?.name || s.material_id}</td>
                  <td className="text-end">
                    <QtyDual quantityInBase={s.theoretical_quantity} material={mat} />
                  </td>
                  <td className="text-end">
                    <QtyDual quantityInBase={s.actual_quantity} material={mat} />
                  </td>
                  <td className="text-end">
                    {/* #232210 (AC5): dùng effective_threshold (override ?? mặc định chung), không
                        đọc thẳng material.min_stock_threshold — bug đọc nhầm nguồn khi override bật. */}
                    <QtyDual quantityInBase={s.effective_threshold} material={mat} />
                    <div>
                      {s.min_stock_override != null ? (
                        <Badge bg="info" className="mt-1">Riêng</Badge>
                      ) : (
                        <Badge bg="secondary" className="mt-1">Chung</Badge>
                      )}
                    </div>
                  </td>
                  <td className="text-end">
                    {/* #151257 — cùng khuôn ngưỡng tối thiểu: effective_par_level = override ?? mức chung. */}
                    {s.effective_par_level != null ? (
                      <QtyDual quantityInBase={s.effective_par_level} material={mat} />
                    ) : '—'}
                    <div>
                      {s.par_level_override != null ? (
                        <Badge bg="info" className="mt-1">Riêng</Badge>
                      ) : (
                        <Badge bg="secondary" className="mt-1">Chung</Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    {/* #011206 — tồn ÂM là SAI DỮ LIỆU, không phải "sắp hết": phải phân biệt để
                        người kiểm kho biết là cần đếm lại chứ không phải đi mua bù. */}
                    {Number(s.theoretical_quantity) < 0 ? (
                      <Badge bg="danger">Tồn âm</Badge>
                    ) : s.is_low_stock ? (
                      <Badge bg="danger">Sắp hết</Badge>
                    ) : (
                      <Badge bg="success">OK</Badge>
                    )}
                  </td>
                  <td className="small text-muted">{fmtDate(s.last_stocktake_at)}</td>
                  <td className="text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => setHistoryMaterial(mat)}
                    >
                      Lịch sử
                    </Button>
                    {/* #011206 — đường sửa tồn ngay tại màn kho. Không giới hạn Admin: người kiểm
                        kho của chi nhánh phải tự nắn được số, nếu không thì lại đúng vòng luẩn
                        quẩn "thấy sai mà không ai sửa được". */}
                    <Button
                      variant={Number(s.theoretical_quantity) < 0 ? 'danger' : 'outline-secondary'}
                      size="sm"
                      className="me-1"
                      onClick={() => setCountStock({ ...s, material: mat })}
                    >
                      Kiểm kê khởi tạo
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => setOverrideStock({ ...s, material: mat })}
                      >
                        Sửa ngưỡng riêng
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setParLevelOverrideStock({ ...s, material: mat })}
                      >
                        Sửa định mức riêng
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <ImportStockModal show={showImport} onHide={() => setShowImport(false)}
        branchId={branchId} materials={allMaterials} suppliers={suppliers} onSuccess={handleModalSuccess} />
      <BulkImportModal show={showBulk} onHide={() => setShowBulk(false)}
        branchId={branchId} materials={allMaterials} suppliers={suppliers} onSuccess={handleModalSuccess} />
      <TransferStockModal show={showTransfer} onHide={() => setShowTransfer(false)}
        fromBranchId={branchId} branches={branches} materials={allMaterials} onSuccess={handleModalSuccess} />
      <InitialStockCountModal
        show={!!countStock}
        onHide={() => setCountStock(null)}
        branchId={branchId}
        branchName={branches?.find((b) => b.id === branchId)?.name || ''}
        stock={countStock}
        onSuccess={() => { setCountStock(null); fetchStock(); if (onRefresh) onRefresh(); }}
      />
      <MaterialTransactionsModal
        show={!!historyMaterial}
        onHide={() => setHistoryMaterial(null)}
        branchId={branchId}
        material={historyMaterial}
      />
      {isAdmin && (
        <BranchMinStockOverrideModal
          show={!!overrideStock}
          onHide={() => setOverrideStock(null)}
          branchId={branchId}
          branchName={branches?.find((b) => b.id === branchId)?.name || ''}
          stock={overrideStock}
          onSuccess={() => { setOverrideStock(null); fetchStock(); }}
        />
      )}
      {isAdmin && (
        <BranchParLevelOverrideModal
          show={!!parLevelOverrideStock}
          onHide={() => setParLevelOverrideStock(null)}
          branchId={branchId}
          branchName={branches?.find((b) => b.id === branchId)?.name || ''}
          stock={parLevelOverrideStock}
          onSuccess={() => { setParLevelOverrideStock(null); fetchStock(); }}
        />
      )}
      {isAdmin && (
        <ApplyRatioModal
          show={showApplyRatio}
          onHide={() => setShowApplyRatio(false)}
          branches={branches}
          currentBranchId={branchId}
          materials={allMaterials}
          onSuccess={() => { fetchStock(); if (onRefresh) onRefresh(); }}
        />
      )}
    </>
  );
}

// ── Tab 2: Materials CRUD ────────────────────────────────────────────────────

/**
 * #125244 — bộ lọc của tab "Danh mục NVL" lên URL (KHUON-CHUAN-URL-PARAM.md mục 5, hạng mục
 * `/inventory/:tabKey` — hạng mục DUY NHẤT của bảng admin còn sót lại sau #120936).
 *
 * `tab` KHÔNG phải query param ở màn này: từ #194334 mỗi tab đã là một ROUTE riêng
 * (`/inventory/materials`), và route cũ `/inventory?tab=xxx` vẫn được `InventoryLegacyRedirect`
 * đổi hướng sang route mới. Thêm `?tab=` nữa là đẻ ra hai nguồn sự thật cho cùng một thứ.
 *
 * `branch` do `BranchContext` quản ở cấp toàn cục (#120936), không khai lại ở đây.
 *
 * Bộ tên đã được `dev-advance` chốt ở #131331: ô tìm dùng `q` (thống nhất cả 4 mảng, `search`
 * chỉ còn là alias CHỈ-ĐỌC), còn `branch_id` được giữ làm NGOẠI LỆ chính thức cho riêng kho
 * admin — vì `?branch=` ở 2 màn inventory kia là bộ lọc PHẠM VI TRANG, còn `branch_id` là chi
 * nhánh TOÀN CỤC của `BranchContext`; hai phạm vi khác nhau nên phải hai tên khác nhau.
 */
const MATERIALS_FILTER_SPEC = {
  q: { default: '', alias: ['search'], debounce: 350 },
  category: { default: '' },
  active: {
    default: '',
    valid: (v) => ['active', 'inactive'].includes(v.toLowerCase()),
    parse: (v) => v.toLowerCase(),
  },
  type: {
    default: '',
    valid: (v) => ['RAW', 'SEMI_FINISHED'].includes(v.toUpperCase()),
    parse: (v) => v.toUpperCase(),
  },
};

function MaterialsTab({ materials, loading, onRefresh }) {
  const [filters, setFilters, resetFilters, { ignored, dismissIgnored }] =
    useFilterParams(MATERIALS_FILTER_SPEC);
  const { q, category: categoryFilter, active: activeFilter, type: typeFilter } = filters;
  const setSearch = (v) => setFilters({ q: v });
  const setCategoryFilter = (v) => setFilters({ category: v });
  const setActiveFilter = (v) => setFilters({ active: v });
  const setTypeFilter = (v) => setFilters({ type: v });
  const [showModal, setShowModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);

  const categories = [...new Set(materials.map((m) => m.category).filter(Boolean))];

  const filtered = materials.filter((m) => {
    if (q && !m.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (categoryFilter && m.category !== categoryFilter) return false;
    if (activeFilter === 'active' && !m.is_active) return false;
    if (activeFilter === 'inactive' && m.is_active) return false;
    if (typeFilter && (m.type || 'RAW') !== typeFilter) return false;
    return true;
  });

  const handleEdit = (material) => { setEditingMaterial(material); setShowModal(true); };
  const handleAdd = () => { setEditingMaterial(null); setShowModal(true); };

  const handleDelete = async (material) => {
    if (!window.confirm(`Xóa NVL "${material.name}"?`)) return;
    try {
      await inventoryAPI.deleteMaterial(material.id);
      toast.success('Đã xóa NVL');
      onRefresh();
    } catch (err) {
      toast.error(err?.message || 'Không thể xóa');
    }
  };

  const hasFilter = Boolean(q || categoryFilter || activeFilter || typeFilter);

  return (
    <>
      <InvalidParamsNotice ignored={ignored} onDismiss={dismissIgnored} />

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Form.Control
          size="sm" style={{ maxWidth: 220 }} placeholder="Tìm NVL..."
          value={q} onChange={(e) => setSearch(e.target.value)}
        />
        <Form.Select size="sm" style={{ maxWidth: 180 }} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">Tất cả danh mục</option>
          {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
        </Form.Select>
        <Form.Select size="sm" style={{ maxWidth: 140 }} value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="">Tất cả</option>
          <option value="active">Đang hoạt động</option>
          <option value="inactive">Đã tắt</option>
        </Form.Select>
        <Form.Select size="sm" style={{ maxWidth: 140 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Tất cả loại</option>
          <option value="RAW">Nguyên liệu thô</option>
          <option value="SEMI_FINISHED">Bán thành phẩm</option>
        </Form.Select>
        {hasFilter && (
          <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
            Xoá bộ lọc
          </Button>
        )}
        <Button variant="primary" size="sm" className="ms-auto" onClick={handleAdd}>+ Thêm NVL</Button>
      </div>

      {loading ? <div className="text-center py-5"><Spinner /></div> : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Tên NVL</th>
              <th>Loại</th>
              <th>Đơn vị</th>
              <th>Danh mục</th>
              <th className="text-end">Ngưỡng tối thiểu</th>
              <th className="text-end">Định mức</th>
              <th>Kiểm kê</th>
              <th>Trạng thái</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className={!m.is_active ? 'text-muted' : ''}>
                <td className="fw-medium">{m.name}</td>
                <td>
                  {m.type === 'SEMI_FINISHED'
                    ? <Badge bg="warning" text="dark">BTP</Badge>
                    : <Badge bg="secondary">Thô</Badge>}
                </td>
                <td>{m.unit}</td>
                <td>{m.category ? (CATEGORY_LABELS[m.category] || m.category) : '—'}</td>
                <td className="text-end">
                  {/* #232210: hiển thị theo quy cách đóng gói thay vì gam/ml thô — tái dùng QtyDual
                      (đã dùng cho cột "NVL dưới ngưỡng" ở BranchStockTab), không đẻ khuôn mới. */}
                  {m.min_stock_threshold != null ? <QtyDual quantityInBase={m.min_stock_threshold} material={m} /> : '—'}
                </td>
                <td className="text-end">
                  {/* #151257 — cùng khuôn ngưỡng tối thiểu, dùng đúng QtyDual với m.par_level. */}
                  {m.par_level != null ? <QtyDual quantityInBase={m.par_level} material={m} /> : '—'}
                </td>
                <td>{m.requires_stocktake ? <Badge bg="info">Có</Badge> : '—'}</td>
                <td>
                  <Badge bg={m.is_active ? 'success' : 'secondary'}>
                    {m.is_active ? 'Hoạt động' : 'Đã tắt'}
                  </Badge>
                </td>
                <td>
                  <Button variant="link" size="sm" className="p-0 me-2" onClick={() => handleEdit(m)}>Sửa</Button>
                  <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => handleDelete(m)}>Xóa</Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              // Mục 2.3 của khuôn: trạng thái rỗng phải nói RÕ vì sao, không để người dán link
              // tưởng chi nhánh này thật sự không có NVL nào. (colSpan cũ là 8 trong khi bảng
              // có 9 cột — sửa luôn cho dòng này không bị hụt.)
              <tr>
                <td colSpan={9} className="text-center text-muted py-3">
                  {hasFilter
                    ? 'Không có NVL nào khớp bộ lọc hiện tại. Bấm "Xoá bộ lọc" ở trên để xem lại toàn bộ.'
                    : 'Không có NVL nào'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <MaterialFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        material={editingMaterial}
        onSuccess={() => { setShowModal(false); onRefresh(); }}
      />
    </>
  );
}

// ── Tab 3: Suppliers CRUD ────────────────────────────────────────────────────

function SuppliersTab({ suppliers, loading, onRefresh, allMaterials }) {
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showNVLModal, setShowNVLModal] = useState(false);
  const [selectedSupplierForNVL, setSelectedSupplierForNVL] = useState(null);

  const handleEdit = (s) => { setEditingSupplier(s); setShowModal(true); };
  const handleAdd = () => { setEditingSupplier(null); setShowModal(true); };
  const handleManageNVL = (s) => { setSelectedSupplierForNVL(s); setShowNVLModal(true); };

  return (
    <>
      <div className="d-flex mb-3">
        <Button variant="primary" size="sm" className="ms-auto" onClick={handleAdd}>+ Thêm NCC</Button>
      </div>

      {loading ? <div className="text-center py-5"><Spinner /></div> : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Tên nhà cung cấp</th>
              <th>SĐT</th>
              <th>SĐT Zalo</th>
              <th>Ghi chú</th>
              <th>Trạng thái</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.id}>
                <td className="fw-medium">{s.name}</td>
                <td>{s.phone || '—'}</td>
                <td>
                  {s.zalo_phone
                    ? s.zalo_phone
                    : <span className="text-muted small">Chưa có</span>}
                </td>
                <td className="text-muted small">{s.note || '—'}</td>
                <td>
                  <Badge bg={s.is_active ? 'success' : 'secondary'}>
                    {s.is_active ? 'Hoạt động' : 'Đã tắt'}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button variant="link" size="sm" className="p-0" onClick={() => handleEdit(s)}>Sửa</Button>
                    <Button variant="outline-info" size="sm" onClick={() => handleManageNVL(s)}>
                      Quản lý NVL
                    </Button>
                    <Link
                      className="btn btn-outline-success btn-sm"
                      to={`/inventory/supplier-order-zalo?supplier_id=${s.id}`}
                    >
                      Gửi đơn Zalo
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {suppliers.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-3">Chưa có nhà cung cấp</td></tr>
            )}
          </tbody>
        </Table>
      )}

      <SupplierFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        supplier={editingSupplier}
        onSuccess={() => { setShowModal(false); onRefresh(); }}
      />

      <SupplierMaterialModal
        show={showNVLModal}
        onHide={() => setShowNVLModal(false)}
        supplier={selectedSupplierForNVL}
        allMaterials={allMaterials}
      />
    </>
  );
}

// ── Tab 4: Recipes ───────────────────────────────────────────────────────────

function getCoverageStatus(item) {
  if (!item.has_base_recipe) return { tone: 'danger', label: 'Chưa có CT gốc' };
  if (item.total_options === 0) return { tone: 'success', label: '✓ CT đầy đủ' };
  if (item.configured_options === item.total_options) {
    return { tone: 'success', label: `✓ ${item.configured_options}/${item.total_options}` };
  }
  if (item.configured_options === 0) {
    return { tone: 'danger', label: `0/${item.total_options} · Thiếu adjustment` };
  }
  return { tone: 'warning', label: `${item.configured_options}/${item.total_options}` };
}

function RecipesTab({ materials, focusMenuItemId }) {
  const [menuItems, setMenuItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const reloadCoverage = async () => {
    try {
      const data = await inventoryAPI.getRecipeCoverage();
      setMenuItems(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      toast.error(err?.message || 'Không tải được độ phủ công thức');
    }
  };

  useEffect(() => {
    reloadCoverage();
  }, []);

  // Auto-select item from URL param after list loads
  useEffect(() => {
    if (!focusMenuItemId || !menuItems.length || selectedItem) return;
    const item = menuItems.find((i) => i.id === focusMenuItemId);
    if (item) loadRecipe(item);
  }, [focusMenuItemId, menuItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRecipe = async (item) => {
    setSelectedItem(item);
    setRecipe(null);
    try {
      setLoading(true);
      const data = await inventoryAPI.getRecipe(item.id);
      setRecipe(data);
    } catch {
      setRecipe(null);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = menuItems.filter((m) => {
    if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (onlyMissing) {
      const incomplete = !m.has_base_recipe || m.configured_options < m.total_options;
      if (!incomplete) return false;
    }
    return true;
  });

  const handleRecipeSaved = async () => {
    setShowModal(false);
    if (selectedItem) await loadRecipe(selectedItem);
    await reloadCoverage();
  };

  return (
    <Row>
      <Col md={4} className="border-end">
        <Form.Control
          size="sm" className="mb-2" placeholder="Tìm món..."
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <Form.Check
          type="checkbox"
          id="filter-missing-recipe"
          label="Chỉ hiện món thiếu công thức"
          className="mb-2 small"
          checked={onlyMissing}
          onChange={(e) => setOnlyMissing(e.target.checked)}
        />
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          {filteredItems.map((item) => {
            const status = getCoverageStatus(item);
            const isSelected = selectedItem?.id === item.id;
            const isFocused = !isSelected && item.id === focusMenuItemId;
            return (
              <div
                key={item.id}
                className={`p-2 border-bottom cursor-pointer ${isSelected ? 'bg-primary text-white' : isFocused ? 'bg-warning bg-opacity-25 border-warning' : 'hover-bg'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => loadRecipe(item)}
              >
                <div className="d-flex align-items-center gap-2">
                  <div className="fw-medium small flex-grow-1">{item.name}</div>
                  <Badge bg={isSelected ? 'light' : status.tone} text={isSelected ? 'dark' : undefined}>
                    {status.label}
                  </Badge>
                </div>
                <div className="small opacity-75">
                  {item.category?.name}
                  {!item.has_base_recipe && item.total_options > 0 && ' · cần CT gốc'}
                </div>
              </div>
            );
          })}
          {filteredItems.length === 0 && (
            <p className="text-muted small text-center py-3 mb-0">Không có món nào</p>
          )}
        </div>
      </Col>
      <Col md={8}>
        {!selectedItem ? (
          <p className="text-muted text-center py-5">Chọn món để xem/sửa công thức</p>
        ) : loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : (
          <>
            <div className="d-flex align-items-center mb-3">
              <h6 className="mb-0">{selectedItem.name}</h6>
              <Button size="sm" variant="outline-primary" className="ms-auto"
                onClick={() => setShowModal(true)}>
                {recipe?.recipes?.length ? 'Sửa công thức' : 'Tạo công thức'}
              </Button>
            </div>

            {!recipe?.recipes?.length ? (
              <p className="text-muted">Chưa có công thức cho món này</p>
            ) : (
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Nguyên liệu</th>
                    <th className="text-end">Số lượng</th>
                    <th>Đơn vị</th>
                    <th>Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {recipe.recipes.map((ing, i) => (
                    <tr key={i}>
                      <td>{ing.material?.name || ing.material_id}</td>
                      <td className="text-end">{fmtQty(ing.quantity)}</td>
                      <td>{ing.material?.unit}</td>
                      <td className="small text-muted">{ing.unit_note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Col>

      <RecipeFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        menuItem={selectedItem}
        recipe={recipe}
        materials={materials}
        onSuccess={handleRecipeSaved}
      />
    </Row>
  );
}

// ── Tab 5: Stocktake History ─────────────────────────────────────────────────

const STOCKTAKE_SUSPICIOUS_REASON_LABELS = {
  exact_match_all: 'Số nhập trùng khít tuyệt đối toàn bộ NVL',
  too_fast: 'Nhập quá nhanh so với số dòng NVL',
  repeated_edits: 'Sửa lại số nhiều lần trong thời gian ngắn',
};

// #175019 — chi nhánh đến từ THANH TRÊN ĐẦU (`BranchContext`), tab không còn ô chọn riêng.
function StocktakeTab({ branchId, materials }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const branchFilter = branchId || '';
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // #013556 (lệnh sếp 24/08/2026): màn hậu kiểm — lọc theo nhân viên (tên) + chỉ hiện phiên bị
  // gắn cờ "kiểm ẩu".
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [onlySuspicious, setOnlySuspicious] = useState(false);
  const [detailSession, setDetailSession] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [cancellingSession, setCancellingSession] = useState(null);

  const materialsById = useMemo(
    () => new Map((materials || []).map((m) => [m.id, m])),
    [materials],
  );

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      if (statusFilter) params.status = statusFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (onlySuspicious) params.only_suspicious = 'true';
      const data = await inventoryAPI.getStocktakeSessions(params);
      setSessions(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải lịch sử kiểm kê');
    } finally {
      setLoading(false);
    }
  }, [branchFilter, statusFilter, fromDate, toDate, onlySuspicious]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const visibleSessions = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => (s.employee?.full_name || '').toLowerCase().includes(q));
  }, [sessions, employeeSearch]);

  const openDetail = async (session) => {
    try {
      setLoadingDetail(true);
      setShowDetail(true);
      const data = await inventoryAPI.getStocktakeSession(session.id);
      setDetailSession(data?.session || data);
    } catch {
      toast.error('Không thể tải chi tiết');
      setShowDetail(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCancelSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Force huỷ phiên kiểm kê đang kẹt?\nChi nhánh sẽ có thể tạo phiên kiểm kê mới.')) return;
    try {
      setCancellingSession(sessionId);
      const result = await inventoryAPI.cancelStocktakeSession(sessionId);
      toast.success(result?.message || 'Đã huỷ phiên kiểm kê');
      fetchSessions();
    } catch (err) {
      toast.error(err?.message || 'Không thể huỷ phiên');
    } finally {
      setCancellingSession(null);
    }
  };

  return (
    <>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Form.Select size="sm" style={{ maxWidth: 150 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Tất cả trạng thái</option>
          <option value="IN_PROGRESS">Đang kiểm</option>
          <option value="COMPLETED">Hoàn thành</option>
          <option value="CANCELLED">Đã huỷ</option>
        </Form.Select>
        <Form.Control size="sm" type="date" style={{ maxWidth: 140 }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <Form.Control size="sm" type="date" style={{ maxWidth: 140 }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        <Form.Control
          size="sm"
          type="text"
          placeholder="Lọc theo tên nhân viên..."
          style={{ maxWidth: 200 }}
          value={employeeSearch}
          onChange={(e) => setEmployeeSearch(e.target.value)}
        />
        <Form.Check
          type="checkbox"
          id="stocktake-only-suspicious"
          label="Chỉ hiện phiên bị gắn cờ kiểm ẩu"
          checked={onlySuspicious}
          onChange={(e) => setOnlySuspicious(e.target.checked)}
        />
      </div>

      {loading ? <div className="text-center py-5"><Spinner /></div> : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Chi nhánh</th>
              <th>Nhân viên</th>
              <th>Trạng thái</th>
              <th className="text-end">Tổng NVL</th>
              <th className="text-end">Có chênh lệch</th>
              <th>Ngày tạo</th>
              <th>Hoàn thành</th>
              <th>Kiểm ẩu</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleSessions.map((s) => {
              const statusBg = s.status === 'COMPLETED' ? 'success' : s.status === 'CANCELLED' ? 'secondary' : 'warning';
              const statusLabel = s.status === 'COMPLETED' ? 'Hoàn thành' : s.status === 'CANCELLED' ? 'Đã huỷ' : 'Đang kiểm';
              return (
                <tr
                  key={s.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openDetail(s)}
                  className={s.is_suspicious ? 'table-danger' : ''}
                >
                  <td>{s.branch?.name}</td>
                  <td>{s.employee?.full_name || '—'}</td>
                  <td>
                    <Badge bg={statusBg}>{statusLabel}</Badge>
                  </td>
                  <td className="text-end">{s.total_items ?? '—'}</td>
                  <td className="text-end">
                    {s.items_with_variance != null ? (
                      <span className={s.items_with_variance > 0 ? 'text-danger' : 'text-success'}>
                        {s.items_with_variance}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="small">{fmtDateTime(s.created_at)}</td>
                  <td className="small">{fmtDateTime(s.completed_at)}</td>
                  <td title={(s.suspicious_reasons || []).map((r) => STOCKTAKE_SUSPICIOUS_REASON_LABELS[r] || r).join('; ')}>
                    {s.is_suspicious ? <Badge bg="danger">⚠ Kiểm ẩu</Badge> : <span className="text-muted">—</span>}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {s.status === 'IN_PROGRESS' && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                        disabled={cancellingSession === s.id}
                        onClick={(e) => handleCancelSession(e, s.id)}
                        title="Force huỷ phiên kẹt"
                      >
                        {cancellingSession === s.id ? <Spinner size="sm" /> : 'Force huỷ'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visibleSessions.length === 0 && (
              <tr><td colSpan={9} className="text-center text-muted py-3">Không có phiên kiểm kê nào khớp bộ lọc</td></tr>
            )}
          </tbody>
        </Table>
      )}

      <StocktakeDetailModal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        session={loadingDetail ? null : detailSession}
        materialsById={materialsById}
      />
    </>
  );
}

// ── Tab 6: Reports ───────────────────────────────────────────────────────────

function ReportsTab({ branchId }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!branchId) return;
    try {
      setLoading(true);
      const data = await inventoryAPI.getDashboard(branchId);
      setDashboard(data);
    } catch {
      // Dashboard may not be implemented yet
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (!branchId) return <p className="text-muted py-4 text-center">Vui lòng chọn chi nhánh</p>;

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <Row className="g-3">
      <Col md={3}>
        <Card className="text-center h-100">
          <Card.Body>
            <div className="display-6 fw-bold text-primary">{dashboard?.total_materials ?? '—'}</div>
            <div className="small text-muted mt-1">Tổng NVL</div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="text-center h-100 border-danger">
          <Card.Body>
            <div className="display-6 fw-bold text-danger">{dashboard?.low_stock_count ?? '—'}</div>
            <div className="small text-muted mt-1">NVL sắp hết</div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="text-center h-100 border-success">
          <Card.Body>
            <div className="display-6 fw-bold text-success">{dashboard?.imports_today ?? '—'}</div>
            <div className="small text-muted mt-1">Lần nhập kho hôm nay</div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="text-center h-100 border-warning">
          <Card.Body>
            <div className="display-6 fw-bold text-warning">{dashboard?.pending_stocktake ?? '—'}</div>
            <div className="small text-muted mt-1">Phiên KK đang chờ</div>
          </Card.Body>
        </Card>
      </Col>

      {!dashboard && (
        <Col xs={12}>
          <Card className="border-0 bg-light">
            <Card.Body className="text-center text-muted py-5">
              <p>Báo cáo chi tiết sẽ hiển thị ở đây khi API dashboard được kết nối.</p>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
}

// Khối "Báo cáo kho" gộp bên trong tab "Tồn kho" (#220622, thay cho mục menu riêng cũ) — gập/mở,
// nằm SAU bảng tồn kho để không đẩy bảng tồn kho xuống dưới, đặc biệt trên mobile 390x844.
function StockReportsPanel({ branchId, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);

  return (
    <Card className="mt-4">
      <Card.Header
        className="d-flex align-items-center justify-content-between"
        role="button"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="fw-semibold">📊 Báo cáo kho</span>
        <small className="material-icons">{open ? 'expand_less' : 'expand_more'}</small>
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body>
            <ReportsTab branchId={branchId} />
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
}

// ── Tab 7: Import Requests ────────────────────────────────────────────────────

const STATUS_COLOR = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };
const STATUS_LABEL = { PENDING: 'Chờ duyệt', APPROVED: 'Đã duyệt', REJECTED: 'Từ chối' };
const fmtCurrency = (n) => n != null ? Number(n).toLocaleString('vi-VN') + 'đ' : '—';

/**
 * #144242 — bộ lọc của tab "Phiếu nhập" lên URL, để ô thống kê phiếu nhập hàng trên Dashboard
 * bấm được thẳng sang đây với đúng trạng thái ĐÃ LỌC SẴN (không mở màn trống rồi bắt người
 * dùng lọc lại). Dùng lại `useFilterParams` + `InvalidParamsNotice` của #120936/#125244.
 *
 * Tên tham số theo KHUON-CHUAN-URL-PARAM.md:
 * - `status` — enum HOA, khớp đúng `enum ImportRequestStatus` của server.
 *
 * #175019 (lệnh sếp qua NĐH) — bộ lọc chi nhánh RIÊNG của tab ĐÃ BỎ. Chi nhánh nay chỉ đến từ
 * MỘT nơi duy nhất: thanh chọn chi nhánh trên đầu (`BranchContext`, param toàn cục `branch_id`).
 * Vì vậy `branch` không còn trong spec dưới đây. Link cũ mang `?branch=` không còn nghĩa ở màn
 * này (`readValues` chỉ đọc param có trong spec); nơi dẫn tới đây — ô thống kê Phiếu nhập trên
 * Dashboard — đã đổi sang `branch_id` để link dán lại vẫn mở đúng chi nhánh.
 */
/**
 * #191439 — Tab "Phiếu nhập" là ĐÍCH của link sâu trong 2 tin phiếu nhập kho (dòng 116, 117
 * của bảng khảo sát #132320): phiếu tự động duyệt do quá hạn (phải hậu kiểm) và phiếu mới chờ
 * duyệt. Tin gửi `?branch_id=&q=<số phiếu>&status=`.
 *
 * `q` lọc PHÍA TRÌNH DUYỆT theo mã phiếu: API `getImportRequests` không có tham số tìm tự do,
 * nhưng tab vốn đã tải sẵn cả danh sách (`limit: 100`) nên lọc trên dữ liệu đã có là đủ và
 * không thêm lời gọi API nào.
 */
const IMPORT_REQUESTS_FILTER_SPEC = {
  status: {
    default: 'PENDING',
    valid: (v) => ['PENDING', 'APPROVED', 'REJECTED', ''].includes(v.toUpperCase()),
    parse: (v) => v.toUpperCase(),
  },
  q: { default: '', alias: ['search'], debounce: 350 },
};

function ImportRequestsTab({ branchId, currentUserId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters, resetFilters, { ignored, dismissIgnored }] =
    useFilterParams(IMPORT_REQUESTS_FILTER_SPEC);
  const { status: statusFilter, q: requestQuery } = filters;
  const branchFilter = branchId || '';
  const setStatusFilter = (v) => setFilters({ status: v });
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [showReject, setShowReject] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (branchFilter) params.branch_id = branchFilter;
      params.limit = 100;
      const data = await inventoryAPI.getImportRequests(params);
      setItems(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải phiếu nhập');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const handleApproveInline = async (req) => {
    if (!currentUserId) return;
    try {
      await inventoryAPI.approveImportRequest(req.id, currentUserId);
      toast.success(`Đã duyệt ${req.request_number}`);
      load();
    } catch (err) {
      toast.error(err?.message || 'Duyệt thất bại');
    }
  };

  const openReject = (req) => {
    setRejectTarget(req);
    setShowReject(true);
  };

  // #191439: lọc theo mã phiếu lấy từ `?q=`, không phân biệt hoa/thường.
  const visibleItems = useMemo(() => {
    const needle = (requestQuery || '').trim().toLowerCase();
    if (!needle) return items;
    return items.filter((req) =>
      String(req.request_number ?? '').toLowerCase().includes(needle),
    );
  }, [items, requestQuery]);

  const hasFilter = Boolean(statusFilter) || Boolean(requestQuery);

  return (
    <>
      <InvalidParamsNotice ignored={ignored} onDismiss={dismissIgnored} />

      <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
        <Form.Select
          size="sm"
          style={{ width: 150 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Tất cả trạng thái</option>
          <option value="PENDING">Chờ duyệt</option>
          <option value="APPROVED">Đã duyệt</option>
          <option value="REJECTED">Từ chối</option>
        </Form.Select>
        <Form.Control
          size="sm"
          style={{ width: 200 }}
          placeholder="Tìm theo mã phiếu"
          value={requestQuery}
          onChange={(e) => setFilters({ q: e.target.value })}
        />
        <Button variant="outline-secondary" size="sm" onClick={load}>↻ Làm mới</Button>
        {hasFilter && (
          <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
            Xoá bộ lọc
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : visibleItems.length === 0 ? (
        <p className="text-muted text-center py-4">
          {hasFilter
            ? 'Không có phiếu nhập nào khớp bộ lọc hiện tại. Bấm "Xoá bộ lọc" ở trên để xem lại toàn bộ.'
            : 'Không có phiếu nhập nào'}
        </p>
      ) : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Mã phiếu</th>
              <th>Chi nhánh</th>
              <th>Người tạo</th>
              <th>Trạng thái</th>
              <th className="text-end">Tổng tiền</th>
              <th>Ngày tạo</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((req) => (
              <tr key={req.id}>
                <td>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none fw-semibold"
                    onClick={() => { setSelected(req.id); setShowDetail(true); }}
                  >
                    {req.request_number}
                  </Button>
                </td>
                <td>{req.branch?.name}</td>
                <td>{req.creator?.full_name}</td>
                <td>
                  <Badge bg={STATUS_COLOR[req.status] || 'secondary'}>
                    {STATUS_LABEL[req.status] || req.status}
                  </Badge>
                </td>
                <td className="text-end">{fmtCurrency(req.total_amount)}</td>
                <td>{fmtDate(req.created_at)}</td>
                <td>
                  {req.status === 'PENDING' && (
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleApproveInline(req)}
                      >
                        Duyệt
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openReject(req)}
                      >
                        Từ chối
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <ImportRequestDetailModal
        show={showDetail}
        requestId={selected}
        currentUserId={currentUserId}
        onHide={() => setShowDetail(false)}
        onApprove={load}
        onReject={(req) => { openReject(req); }}
      />

      <RejectImportRequestModal
        show={showReject}
        request={rejectTarget}
        currentUserId={currentUserId}
        onHide={() => setShowReject(false)}
        onSuccess={load}
      />
    </>
  );
}

// ── Tab 8: Material Packages ──────────────────────────────────────────────────

function MaterialPackagesTab({ materials }) {
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editPkg, setEditPkg] = useState(null);

  const loadPackages = useCallback(async () => {
    if (!selectedMaterial) { setPackages([]); return; }
    setLoading(true);
    try {
      const data = await inventoryAPI.getMaterialPackages(selectedMaterial);
      setPackages(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải quy cách');
    } finally {
      setLoading(false);
    }
  }, [selectedMaterial]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const handleDelete = async (pkg) => {
    if (pkg.is_base_unit) {
      toast.error('Không thể xóa đơn vị cơ sở');
      return;
    }
    if (!window.confirm(`Xóa đơn vị "${pkg.name}"?`)) return;
    try {
      await inventoryAPI.deleteMaterialPackage(pkg.id);
      toast.success('Đã xóa đơn vị nhập hàng');
      loadPackages();
    } catch (err) {
      toast.error(err?.message || 'Xóa thất bại');
    }
  };

  const selectedMat = materials.find((m) => m.id === selectedMaterial);

  return (
    <>
      <div className="d-flex gap-2 mb-3 align-items-center">
        <Form.Select
          size="sm"
          style={{ width: 250 }}
          value={selectedMaterial}
          onChange={(e) => setSelectedMaterial(e.target.value)}
        >
          <option value="">-- Chọn NVL --</option>
          {(materials || []).map((m) => (
            <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
          ))}
        </Form.Select>
        {selectedMaterial && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => { setEditPkg(null); setShowForm(true); }}
          >
            + Thêm đơn vị nhập
          </Button>
        )}
      </div>

      {!selectedMaterial ? (
        <p className="text-muted text-center py-4">Chọn NVL để xem đơn vị nhập hàng</p>
      ) : loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : packages.length === 0 ? (
        <p className="text-muted text-center py-4">Chưa có đơn vị nhập hàng nào</p>
      ) : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Tên đơn vị</th>
              <th>Nhãn ngắn</th>
              <th className="text-end">Quy đổi (→ {selectedMat?.unit})</th>
              <th>Loại</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {[...packages].sort((a, b) => (b.is_base_unit ? 1 : 0) - (a.is_base_unit ? 1 : 0)).map((pkg) => (
              <tr key={pkg.id} className={pkg.is_base_unit ? 'table-light' : ''}>
                <td>
                  {pkg.name}
                  {pkg.is_default && <Badge bg="success" className="ms-1">Mặc định</Badge>}
                </td>
                <td>
                  <code>{pkg.unit_label || '—'}</code>
                </td>
                <td className="text-end font-monospace">
                  {Number(pkg.quantity_in_base).toLocaleString('vi-VN')} {selectedMat?.unit}
                </td>
                <td>
                  {pkg.is_base_unit
                    ? <Badge bg="secondary">Đơn vị cơ sở</Badge>
                    : <Badge bg="outline" className="border text-muted">Nhập hàng</Badge>}
                </td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => { setEditPkg(pkg); setShowForm(true); }}
                    >
                      Sửa
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(pkg)}
                      disabled={pkg.is_base_unit}
                      title={pkg.is_base_unit ? 'Không thể xóa đơn vị cơ sở' : ''}
                    >
                      Xóa
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <MaterialPackageFormModal
        show={showForm}
        pkg={editPkg}
        materialId={selectedMaterial}
        materialUnit={selectedMat?.unit}
        onHide={() => setShowForm(false)}
        onSuccess={loadPackages}
      />
    </>
  );
}

// ── Tab 9: BTP Recipe ────────────────────────────────────────────────────────

function BtpRecipeTab({ materials, loading }) {
  const [search, setSearch] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const btpMaterials = materials.filter((m) => m.type === 'SEMI_FINISHED');

  const filtered = btpMaterials.filter((m) =>
    !search || m.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="d-flex gap-2 mb-3 align-items-center">
        <Form.Control
          size="sm" style={{ maxWidth: 260 }} placeholder="Tìm bán thành phẩm..."
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-muted small ms-auto">
          {btpMaterials.length} bán thành phẩm
        </span>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : btpMaterials.length === 0 ? (
        <p className="text-muted text-center py-4">
          Chưa có bán thành phẩm nào. Hãy tạo NVL với loại "Bán thành phẩm" trước.
        </p>
      ) : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Tên BTP</th>
              <th>Đơn vị</th>
              <th>Danh mục</th>
              <th>Trạng thái</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id}>
                <td className="fw-medium">{m.name}</td>
                <td>{m.unit}</td>
                <td>{m.category ? (CATEGORY_LABELS[m.category] || m.category) : '—'}</td>
                <td>
                  <Badge bg={m.is_active ? 'success' : 'secondary'}>
                    {m.is_active ? 'Hoạt động' : 'Đã tắt'}
                  </Badge>
                </td>
                <td>
                  <Button variant="outline-primary" size="sm" onClick={() => setSelectedMaterial(m)}>
                    Cấu hình công thức
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted py-3">Không tìm thấy BTP</td></tr>
            )}
          </tbody>
        </Table>
      )}

      {selectedMaterial && (
        <BtpRecipeModal
          material={selectedMaterial}
          onClose={() => setSelectedMaterial(null)}
        />
      )}
    </>
  );
}

// ── Tab 10: Production ───────────────────────────────────────────────────────

function ProductionTab({ branches, branchId, onStockChanged }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [filterBranch, setFilterBranch] = useState(branchId || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filterBranch) params.branch_id = filterBranch;
      const res = await inventoryAPI.getProductionLogs(params);
      setLogs(res.data || []);
      setTotal(res.total || 0);
    } catch {
      toast.error('Không thể tải lịch sử sản xuất');
    } finally {
      setLoading(false);
    }
  }, [filterBranch, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (branchId && !filterBranch) setFilterBranch(branchId);
  }, [branchId]);

  const handleModalClose = (refreshed) => {
    setShowModal(false);
    if (refreshed) {
      fetchLogs();
      if (onStockChanged) onStockChanged();
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
        <Form.Select
          size="sm" style={{ maxWidth: 200 }}
          value={filterBranch} onChange={(e) => { setFilterBranch(e.target.value); setPage(1); }}
        >
          <option value="">Tất cả chi nhánh</option>
          {(branches || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Form.Select>
        <Button variant="outline-secondary" size="sm" onClick={fetchLogs}>↻ Làm mới</Button>
        <Button
          variant="success" size="sm" className="ms-auto"
          disabled={!branches?.length}
          onClick={() => setShowModal(true)}
        >
          + Sản xuất BTP
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : logs.length === 0 ? (
        <p className="text-muted text-center py-4">Chưa có lịch sử sản xuất</p>
      ) : (
        <>
          <Table bordered hover responsive size="sm">
            <thead className="table-light">
              <tr>
                <th>Thời gian</th>
                <th>Chi nhánh</th>
                <th>Bán thành phẩm</th>
                <th className="text-end">Số lượng</th>
                <th>Nhân viên</th>
                <th>Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="small text-muted">{fmtDateTime(log.created_at)}</td>
                  <td>{log.branch?.name || log.branch_id}</td>
                  <td className="fw-medium">{log.semi_finished_material?.name}</td>
                  <td className="text-end">
                    {fmtQty(log.quantity_produced)} {log.semi_finished_material?.unit}
                  </td>
                  <td>{log.employee?.full_name || '—'}</td>
                  <td className="text-muted small">{log.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex gap-2 align-items-center justify-content-end mt-2">
              <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
              <span className="small">{page} / {totalPages}</span>
              <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</Button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <ProductionModal branches={branches || []} onClose={handleModalClose} />
      )}
    </>
  );
}

// ── Tab 11: Purchase Orders ───────────────────────────────────────────────────

const PO_STATUS_MAP = {
  DRAFT: { label: 'Nháp', bg: 'secondary' },
  CONFIRMED: { label: 'Đã xác nhận', bg: 'primary' },
  RECEIVING: { label: 'Đang kiểm', bg: 'warning' },
  COMPLETED: { label: 'Hoàn thành', bg: 'success' },
  CANCELLED: { label: 'Đã hủy', bg: 'danger' },
};

// #223048 (lệnh sếp update_id=731599168, 28/08/2026) — lệch SL không còn phải duyệt.
// PENDING_REVIEW chỉ còn ở phiếu CŨ; phiếu lệch mới chốt thẳng COMPLETED + has_variance = true,
// nên phải phân biệt được "đã nhập kho khớp" với "đã nhập kho CÓ LỆCH" ngay trên bảng đơn.
const GR_STATUS_BADGE = {
  IN_PROGRESS: { label: 'Đang kiểm', bg: 'warning' },
  PENDING_REVIEW: { label: 'Chờ duyệt (phiếu cũ) ⚠️', bg: 'danger' },
  COMPLETED: { label: 'Đã nhập kho', bg: 'success' },
};

// #010639 — 4 MỨC phản hồi của NCC cho link xác nhận đơn (BE trả `po.supplier_responses`, mức được
// suy ra ở `supplier-order-response.util.ts` — ở đây CHỈ tô màu, không tự tính lại mức).
//
// Mức 1 và 2 tô đỏ/vàng vì đó chính là nhóm PHẢI GỌI THÚC: trước #010639 admin không phân biệt nổi
// "NCC chưa mở link" với "đã mở nhưng chưa trả lời", nên không biết nên gọi ai.
const NCC_RESPONSE_BADGE = {
  SENT: { label: 'Chưa mở link', bg: 'danger' },
  OPENED: { label: 'Đã mở, chưa trả lời', bg: 'warning' },
  CONFIRMED: { label: 'Đã xác nhận sẽ giao', bg: 'success' },
  CHANGE_PENDING: { label: 'Đề nghị sửa chờ duyệt', bg: 'info' },
  // Đơn phát link TRƯỚC khi có tính năng: KHÔNG có dữ liệu xem/xác nhận. Nói thẳng "không rõ" chứ
  // không gán bừa thành mức 1 — gán bừa là hệ thống khẳng định điều nó không biết.
  UNKNOWN: { label: 'Không rõ (đơn cũ)', bg: 'secondary' },
};

const NCC_RESPONSE_FILTERS = [
  { value: 'NEEDS_NUDGE', label: '⚠️ Cần thúc (mức 1+2)' },
  { value: 'SENT', label: '1. Chưa mở link' },
  { value: 'OPENED', label: '2. Đã mở, chưa trả lời' },
  { value: 'CONFIRMED', label: '3. Đã xác nhận sẽ giao' },
  { value: 'CHANGE_PENDING', label: '4. Đề nghị sửa chờ duyệt' },
  { value: 'UNKNOWN', label: 'Không rõ (đơn cũ)' },
];

/** Chi tiết mốc thời gian — di chuột vào nhãn là biết vì sao đơn đang ở mức đó, khỏi mở đơn ra. */
const nccResponseTooltip = (r) => {
  const at = (d) => (d ? new Date(d).toLocaleString('vi-VN') : null);
  return [
    `${r.supplier_name} — ${r.level_label}`,
    at(r.link_sent_at) && `Gửi link: ${at(r.link_sent_at)}`,
    at(r.first_opened_at) && `Mở lần đầu: ${at(r.first_opened_at)} (${r.open_count} lượt)`,
    at(r.confirmed_at) && `Xác nhận: ${at(r.confirmed_at)}`,
    r.reminder_count > 0 && `Đã nhắc ${r.reminder_count} lần, gần nhất ${at(r.last_reminder_at)}`,
  ]
    .filter(Boolean)
    .join('\n');
};

const grBadge = (gr) => {
  if (!gr) return null;
  const base = GR_STATUS_BADGE[gr.status];
  if (!base) return null;
  if (gr.status === 'COMPLETED' && gr.has_variance) {
    return { label: 'Đã nhập kho · lệch ⚠️', bg: 'info' };
  }
  return base;
};

// #175019 — chi nhánh đến từ thanh trên đầu, bỏ ô chọn riêng của tab.
function PurchaseOrdersTab({ branchId }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const filterBranchId = branchId || '';
  const [filterStatus, setFilterStatus] = useState('');
  // #010639 — lọc theo mức phản hồi NCC; BE chốt mức (`response_level`), FE không tự tính.
  const [filterResponse, setFilterResponse] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterBranchId) params.branch_id = filterBranchId;
      if (filterStatus) params.status = filterStatus;
      if (filterResponse) params.response_level = filterResponse;
      const data = await purchaseOrderAPI.list(params);
      setOrders(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải đơn đặt hàng');
    } finally {
      setLoading(false);
    }
  }, [filterBranchId, filterStatus, filterResponse]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';
  const fmtPrice = (n) => n != null ? Number(n).toLocaleString('vi-VN') + 'đ' : '—';

  return (
    <>
      <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
        <Form.Select size="sm" style={{ width: 160 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">Tất cả trạng thái</option>
          {Object.entries(PO_STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </Form.Select>
        <Form.Select
          size="sm"
          style={{ width: 220 }}
          value={filterResponse}
          onChange={(e) => setFilterResponse(e.target.value)}
          title="Lọc theo phản hồi của NCC với link xác nhận đơn"
        >
          <option value="">NCC phản hồi: tất cả</option>
          {NCC_RESPONSE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </Form.Select>
        <Button variant="outline-secondary" size="sm" onClick={fetchOrders}>↻</Button>
        <Button
          variant="primary"
          size="sm"
          className="ms-auto"
          onClick={() => navigate('/inventory/purchase-order/create')}
        >
          + Tạo đơn đặt hàng
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : orders.length === 0 ? (
        <p className="text-center text-muted py-4">Chưa có đơn đặt hàng nào</p>
      ) : (
        <Table bordered hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Mã đơn</th>
              <th>Chi nhánh</th>
              <th>NCC</th>
              <th>SL NVL</th>
              <th className="text-end">Tổng tiền</th>
              <th>Trạng thái</th>
              <th>NCC phản hồi</th>
              <th>Kiểm hàng</th>
              <th>Ngày tạo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((po) => {
              const status = PO_STATUS_MAP[po.status] ?? { label: po.status, bg: 'secondary' };
              const grStatus = grBadge(po.goods_receipt);
              const suppliers = [...new Set((po.items || []).map((i) => i.supplier?.name).filter(Boolean))];
              return (
                <tr key={po.id} className={po.goods_receipt?.status === 'PENDING_REVIEW' ? 'table-warning' : ''}>
                  <td className="fw-medium">{po.order_number}</td>
                  <td className="small">{po.branch?.name}</td>
                  <td className="small text-muted">{suppliers.slice(0, 2).join(', ') || '—'}{suppliers.length > 2 ? ` +${suppliers.length - 2}` : ''}</td>
                  <td className="text-center">{po.items?.length ?? 0}</td>
                  <td className="text-end">{fmtPrice(po.total_amount)}</td>
                  <td><Badge bg={status.bg}>{status.label}</Badge></td>
                  <td>
                    {/* Một nhãn cho MỖI NCC: đơn trộn NVL của nhiều NCC thì NCC A xác nhận không
                        nói lên gì về NCC B (bài học lỗi Nặng #142217). Chưa gửi link cho ai → "—". */}
                    {(po.supplier_responses || []).length === 0 ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <div className="d-flex flex-column gap-1">
                        {po.supplier_responses.map((r) => {
                          const b = NCC_RESPONSE_BADGE[r.level] ?? { label: r.level, bg: 'secondary' };
                          return (
                            <Badge
                              key={r.supplier_id}
                              bg={b.bg}
                              className="text-wrap text-start"
                              title={nccResponseTooltip(r)}
                            >
                              {po.supplier_responses.length > 1 ? `${r.supplier_name}: ` : ''}{b.label}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td>
                    {grStatus ? <Badge bg={grStatus.bg}>{grStatus.label}</Badge> : <span className="text-muted">—</span>}
                  </td>
                  <td className="small text-muted">{fmtDate(po.created_at)}</td>
                  <td>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => navigate(`/inventory/purchase-order/${po.id}`)}
                    >
                      Xem
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function InventoryManagementPage() {
  const { branches, selectedBranch } = useBranch();
  const { user } = useAuth();
  const location = useLocation();
  const { tabKey } = useParams();

  const [focusMenuItemId] = useState(() =>
    new URLSearchParams(location.search).get('menu_item_id') || null
  );

  // /inventory/stock?section=reports — tự mở khối "Báo cáo kho" (đến từ route cũ /inventory/reports
  // redirect qua, hoặc link chia sẻ trực tiếp), xem StockReportsPanel + REPORTS_TAB_KEY ở trên.
  // KHÔNG dùng useState lazy-init ở đây: khi vào từ /inventory/reports, component này mount lần đầu
  // lúc location.search còn rỗng (trước khi <Navigate> đổi sang ?section=reports), lazy-init sẽ chốt
  // nhầm giá trị cũ. StockReportsPanel bên dưới mới là nơi chốt state 1 lần (mount sau khi đã redirect).
  const autoOpenReports = new URLSearchParams(location.search).get('section') === REPORTS_TAB_KEY;

  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [stockRefreshKey, setStockRefreshKey] = useState(0);

  // #175019 — MỘT nguồn chi nhánh duy nhất: thanh chọn trên đầu. Trước đây trang còn giữ
  // `useState('')` riêng + effect đồng bộ từ header, tạo ra ô "Chi nhánh:" thứ hai ngay dưới
  // tiêu đề (ảnh sếp gửi 29/08/2026). Nay đọc thẳng từ `BranchContext`.
  const branchId = selectedBranch?.id || '';

  const fetchMaterials = useCallback(async () => {
    try {
      setMaterialsLoading(true);
      // include_packages=true để các modal nhập kho có sẵn đơn vị nhập hàng
      const data = await inventoryAPI.getMaterials({ include_packages: true });
      setMaterials(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải danh sách NVL');
    } finally {
      setMaterialsLoading(false);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      setSuppliersLoading(true);
      const data = await inventoryAPI.getSuppliers();
      setSuppliers(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải nhà cung cấp');
    } finally {
      setSuppliersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
    fetchSuppliers();
  }, [fetchMaterials, fetchSuppliers]);

  if (tabKey === REPORTS_TAB_KEY) {
    return <Navigate to={`/inventory/${DEFAULT_TAB_KEY}?section=${REPORTS_TAB_KEY}`} replace />;
  }

  if (!VALID_TAB_KEYS.includes(tabKey)) {
    return <Navigate to={`/inventory/${DEFAULT_TAB_KEY}`} replace />;
  }

  const { title: activeTitle, group: activeGroup } = TAB_META[tabKey];

  let tabContent;
  switch (tabKey) {
    case 'stock':
      tabContent = (
        <>
          <BranchStockTab
            branchId={branchId}
            branches={branches}
            allMaterials={materials}
            suppliers={suppliers}
            onRefresh={fetchMaterials}
            refreshKey={stockRefreshKey}
          />
          <StockReportsPanel branchId={branchId} defaultOpen={autoOpenReports} />
        </>
      );
      break;
    case 'materials':
      tabContent = (
        <MaterialsTab materials={materials} loading={materialsLoading} onRefresh={fetchMaterials} />
      );
      break;
    case 'suppliers':
      tabContent = (
        <SuppliersTab
          suppliers={suppliers}
          loading={suppliersLoading}
          onRefresh={fetchSuppliers}
          allMaterials={materials}
        />
      );
      break;
    case 'recipes':
      tabContent = <RecipesTab materials={materials} focusMenuItemId={focusMenuItemId} />;
      break;
    case 'stocktake':
      tabContent = <StocktakeTab branchId={branchId} materials={materials} />;
      break;
    case 'shrinkage':
      tabContent = <InventoryShrinkageTab branchId={branchId} materials={materials} currentUserId={user?.id} />;
      break;
    case 'shift-material-check':
      tabContent = (
        <ShiftMaterialCheckTab branchId={branchId} branches={branches} materials={materials} currentUserId={user?.id} />
      );
      break;
    case 'import-requests':
      tabContent = <ImportRequestsTab branchId={branchId} currentUserId={user?.id} />;
      break;
    case 'packages':
      tabContent = <MaterialPackagesTab materials={materials} />;
      break;
    case 'btp-recipe':
      tabContent = <BtpRecipeTab materials={materials} loading={materialsLoading} />;
      break;
    case 'purchase-orders':
      tabContent = <PurchaseOrdersTab branchId={branchId} />;
      break;
    default:
      tabContent = null;
  }
  // Tab "Sản xuất BTP" đã ẩn từ trước #194334 — BTP nay là đơn vị ảo, tự cascade xuống NVL thô khi bán,
  // không có route riêng vì không còn hiển thị ở đâu (kể cả tab cũ).

  return (
    <PageLayout>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <div className="small text-muted mb-1">
            <Link to="/inventory/stock" className="text-muted">Quản lý kho & NVL</Link>
            {activeGroup && <> / <span>{activeGroup}</span></>}
            {' / '}<span>{activeTitle}</span>
          </div>
          <h4 className="mb-0">{activeTitle}</h4>
        </div>
      </div>

      {tabContent}
    </PageLayout>
  );
}
